import type {
  CompanionDevice,
  IngestEvent,
  LiveEvent,
  MatchPlayerDetail,
  SessionMatch,
  SessionSummary
} from "./types";

export const mockSession: SessionSummary = {
  session_id: "mock-session",
  group_id: "demo-group",
  started_at: new Date(Date.now() - 62 * 60 * 1000).toISOString(),
  ended_at: null,
  status: "active",
  mvp_player_id: "leo",
  match_count: 5,
  regular_player_ids: ["leo", "max"],
  end_reason: null,
  players: [
    {
      player_id: "leo",
      player_name: "Leo",
      role: "crew",
      current_market_value: 13800000,
      current_rating: 86.4,
      fantasy_points: 126.8,
      market_value_delta: 420000,
      rating_delta: 4.2,
      trend: "hot",
      goals: 12,
      assists: 5,
      saves: 9,
      wins: 4,
      matches: 5,
      current_streak: 4,
      form_label: "in_form",
      peak_market_value: 13800000,
      peak_rating: 86.4,
      fppm: 25.4,
      effective_fppm: 21.6,
      total_matches: 42,
      tier: "Diamant",
      is_regular: true,
      absent_streak: 0,
      first_seen_seq: 1,
      last_seen_seq: 5,
      with_owner_matches: 5,
      vs_owner_matches: 0,
      role_archetype: "striker",
      role_weights: { goals: 0.52, assists: 0.22, saves: 0.26 },
      consistency_sigma: 6.8,
      synergy_with_owner: 0.18,
      fm_score: 24.4,
      fm_role_mult: 1.05,
      fm_consistency_mult: 1.03,
      fm_synergy_mult: 1.05
    },
    {
      player_id: "anton",
      player_name: "Anton",
      role: "owner",
      current_market_value: 12100000,
      current_rating: 78.9,
      fantasy_points: 111.2,
      market_value_delta: 180000,
      rating_delta: 1.1,
      trend: "stable",
      goals: 7,
      assists: 8,
      saves: 11,
      wins: 4,
      matches: 5,
      current_streak: 1,
      form_label: null,
      peak_market_value: 12500000,
      peak_rating: 80.4,
      fppm: 22.2,
      effective_fppm: 19.8,
      total_matches: 38,
      tier: "Gold",
      is_regular: false,
      absent_streak: 0,
      first_seen_seq: 1,
      last_seen_seq: 5,
      with_owner_matches: 0,
      vs_owner_matches: 0,
      role_archetype: "allrounder",
      role_weights: { goals: 0.33, assists: 0.36, saves: 0.31 },
      consistency_sigma: 9.4,
      synergy_with_owner: 0,
      fm_score: 19.9,
      fm_role_mult: 1.0,
      fm_consistency_mult: 1.006,
      fm_synergy_mult: 1.0
    },
    {
      player_id: "max",
      player_name: "Max",
      role: "crew",
      current_market_value: 9400000,
      current_rating: 71.6,
      fantasy_points: 64.4,
      market_value_delta: -210000,
      rating_delta: -2.4,
      trend: "cold",
      goals: 3,
      assists: 6,
      saves: 5,
      wins: 2,
      matches: 3,
      current_streak: -3,
      form_label: "cold",
      peak_market_value: 10200000,
      peak_rating: 74.6,
      fppm: 21.5,
      effective_fppm: 15.9,
      total_matches: 31,
      tier: "Silber",
      is_regular: true,
      absent_streak: 2,
      first_seen_seq: 1,
      last_seen_seq: 3,
      with_owner_matches: 3,
      vs_owner_matches: 0,
      role_archetype: "wall",
      role_weights: { goals: 0.24, assists: 0.27, saves: 0.49 },
      consistency_sigma: 12.6,
      synergy_with_owner: -0.08,
      fm_score: 15.4,
      fm_role_mult: 1.05,
      fm_consistency_mult: 0.974,
      fm_synergy_mult: 0.976
    }
  ]
};

// Mock SessionTimeline data: 5 matches, Max sits out the last two.
export const mockSessionMatches: SessionMatch[] = [
  {
    match_id: "m1",
    match_seq: 1,
    started_at: new Date(Date.now() - 58 * 60 * 1000).toISOString(),
    blue_score: 3,
    orange_score: 2,
    owner_team: "blue",
    present_player_ids: ["anton", "leo", "max", "joe", "jane", "kim"]
  },
  {
    match_id: "m2",
    match_seq: 2,
    started_at: new Date(Date.now() - 46 * 60 * 1000).toISOString(),
    blue_score: 4,
    orange_score: 1,
    owner_team: "blue",
    present_player_ids: ["anton", "leo", "max", "joe", "jane", "kim"]
  },
  {
    match_id: "m3",
    match_seq: 3,
    started_at: new Date(Date.now() - 34 * 60 * 1000).toISOString(),
    blue_score: 2,
    orange_score: 3,
    owner_team: "blue",
    present_player_ids: ["anton", "leo", "max", "joe", "jane", "kim"]
  },
  {
    match_id: "m4",
    match_seq: 4,
    started_at: new Date(Date.now() - 22 * 60 * 1000).toISOString(),
    blue_score: 5,
    orange_score: 3,
    owner_team: "blue",
    present_player_ids: ["anton", "leo", "joe", "jane"]
  },
  {
    match_id: "m5",
    match_seq: 5,
    started_at: new Date(Date.now() - 10 * 60 * 1000).toISOString(),
    blue_score: 3,
    orange_score: 4,
    owner_team: "blue",
    present_player_ids: ["anton", "leo", "joe", "kim"]
  }
];

// Mock per-(match × player) rows — only the latest match is fleshed out so
// the OpponentsBlock has something to render in demo mode. The earlier
// matches just contribute presence info via mockSessionMatches.
export const mockSessionMatchPlayers: MatchPlayerDetail[] = [
  {
    match_id: "m5",
    match_seq: 5,
    started_at: new Date(Date.now() - 10 * 60 * 1000).toISOString(),
    ended_at: new Date(Date.now() - 4 * 60 * 1000).toISOString(),
    blue_score: 3,
    orange_score: 4,
    player_id: "anton",
    player_name: "Anton",
    player_role: "owner",
    team: "blue",
    goals: 1,
    assists: 2,
    saves: 3,
    shots: 5,
    score: 540,
    fantasy_points: 22.2,
    won: false
  },
  {
    match_id: "m5",
    match_seq: 5,
    started_at: new Date(Date.now() - 10 * 60 * 1000).toISOString(),
    ended_at: new Date(Date.now() - 4 * 60 * 1000).toISOString(),
    blue_score: 3,
    orange_score: 4,
    player_id: "leo",
    player_name: "Leo",
    player_role: "crew",
    team: "blue",
    goals: 2,
    assists: 0,
    saves: 1,
    shots: 4,
    score: 470,
    fantasy_points: 26.2,
    won: false
  },
  {
    match_id: "m5",
    match_seq: 5,
    started_at: new Date(Date.now() - 10 * 60 * 1000).toISOString(),
    ended_at: new Date(Date.now() - 4 * 60 * 1000).toISOString(),
    blue_score: 3,
    orange_score: 4,
    player_id: "joe",
    player_name: "Joe",
    player_role: "guest",
    team: "orange",
    goals: 3,
    assists: 1,
    saves: 2,
    shots: 6,
    score: 620,
    fantasy_points: 32.6,
    won: true
  },
  {
    match_id: "m5",
    match_seq: 5,
    started_at: new Date(Date.now() - 10 * 60 * 1000).toISOString(),
    ended_at: new Date(Date.now() - 4 * 60 * 1000).toISOString(),
    blue_score: 3,
    orange_score: 4,
    player_id: "kim",
    player_name: "Kim",
    player_role: "guest",
    team: "orange",
    goals: 1,
    assists: 0,
    saves: 4,
    shots: 3,
    score: 380,
    fantasy_points: 18.3,
    won: true
  }
];

export const mockEvents: LiveEvent[] = [
  {
    id: "1",
    label: "Session erkannt",
    detail: "Leo, Anton und Max spielen seit 62 Minuten zusammen.",
    created_at: new Date(Date.now() - 58 * 60 * 1000).toISOString()
  },
  {
    id: "2",
    label: "Marktwert-Update",
    detail: "Leo steigt nach einem 4-Tore-Spiel um 3.1%.",
    created_at: new Date(Date.now() - 14 * 60 * 1000).toISOString()
  },
  {
    id: "3",
    label: "Live MVP",
    detail: "Anton schiebt sich durch Saves und Assists knapp auf Platz 2.",
    created_at: new Date(Date.now() - 4 * 60 * 1000).toISOString()
  }
];

export const mockDevices: CompanionDevice[] = [
  {
    id: "mock-device",
    device_id: "main-pc",
    device_name: "Gaming PC",
    last_seen_at: new Date(Date.now() - 22 * 1000).toISOString()
  }
];

export const mockIngestEvents: IngestEvent[] = [
  {
    id: "mock-ingest-1",
    device_id: "main-pc",
    event_type: "match_ended",
    raw_event_type: "game:match_ended",
    external_match_id: "demo-match-7",
    players_count: 3,
    accepted: true,
    error: null,
    observed_at: new Date(Date.now() - 45 * 1000).toISOString()
  },
  {
    id: "mock-ingest-2",
    device_id: "main-pc",
    event_type: "match_updated",
    raw_event_type: "game:update",
    external_match_id: "demo-match-7",
    players_count: 3,
    accepted: true,
    error: null,
    observed_at: new Date(Date.now() - 86 * 1000).toISOString()
  }
];
